import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  X, Upload, Image as ImageIcon, Crop as CropIcon, RotateCcw, RotateCw,
  FlipHorizontal, FlipVertical, Clipboard, Eraser, Pipette, Plus, Check, Palette
} from 'lucide-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// ===== Canvas utilities =====

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

/**
 * Rasterize an SVG data URL to a PNG data URL at a reasonable resolution.
 */
const rasterizeSvg = async (svgDataUrl) => {
  const img = await loadImage(svgDataUrl);
  // Use natural dimensions, but scale up small SVGs so they have enough pixels to crop
  let w = img.naturalWidth || 512;
  let h = img.naturalHeight || 512;
  if (w < 256 || h < 256) {
    const scale = Math.max(256 / w, 256 / h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  // Cap at 2048 to avoid huge canvases
  if (w > 2048 || h > 2048) {
    const scale = Math.min(2048 / w, 2048 / h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/png');
  });
};

const applyTransforms = async (imageSrc, transforms, outputConstraints) => {
  const img = await loadImage(imageSrc);
  let w = img.width;
  let h = img.height;

  const rotated = transforms.rotation === 90 || transforms.rotation === 270;
  let canvasW = rotated ? h : w;
  let canvasH = rotated ? w : h;

  if (outputConstraints) {
    const { maxHeight, maxWidth } = outputConstraints;
    let scale = 1;
    if (maxHeight && canvasH > maxHeight) scale = Math.min(scale, maxHeight / canvasH);
    if (maxWidth && canvasW > maxWidth) scale = Math.min(scale, maxWidth / canvasW);
    if (scale < 1) {
      canvasW = Math.round(canvasW * scale);
      canvasH = Math.round(canvasH * scale);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d');

  ctx.save();
  ctx.translate(canvasW / 2, canvasH / 2);
  ctx.rotate((transforms.rotation || 0) * Math.PI / 180);
  if (transforms.flipH) ctx.scale(-1, 1);
  if (transforms.flipV) ctx.scale(1, -1);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);
  ctx.restore();

  return new Promise(resolve => {
    canvas.toBlob(blob => resolve({ blob, width: canvasW, height: canvasH }), 'image/png');
  });
};

const cropImage = async (imageSrc, crop, displayedWidth, displayedHeight, outputSize) => {
  const img = await loadImage(imageSrc);
  const scaleX = img.naturalWidth / displayedWidth;
  const scaleY = img.naturalHeight / displayedHeight;
  const sx = crop.x * scaleX;
  const sy = crop.y * scaleY;
  const sw = crop.width * scaleX;
  const sh = crop.height * scaleY;

  const cropW = Math.round(sw);
  const cropH = Math.round(sh);

  if (outputSize) {
    // Fixed output canvas (e.g. 256x256 for icons) — scale to fit and center
    const canvas = document.createElement('canvas');
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;
    const ctx = canvas.getContext('2d');
    // Scale cropped region to fit within output, preserving aspect ratio
    const scale = Math.min(outputSize.width / cropW, outputSize.height / cropH);
    const drawW = Math.round(cropW * scale);
    const drawH = Math.round(cropH * scale);
    const drawX = Math.round((outputSize.width - drawW) / 2);
    const drawY = Math.round((outputSize.height - drawH) / 2);
    ctx.drawImage(img, sx, sy, sw, sh, drawX, drawY, drawW, drawH);
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  } else {
    // No fixed output — crop to exact pixel dimensions
    const canvas = document.createElement('canvas');
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cropW, cropH);
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }
};

const removeImageBackground = async (imageSrc, targetColor, tolerance = 30) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const { r: bgR, g: bgG, b: bgB } = targetColor;
  const tolSq = tolerance * tolerance;
  const featherRange = Math.max(tolerance * 0.4, 5);
  const featherSq = (tolerance + featherRange) * (tolerance + featherRange);

  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - bgR;
    const dg = data[i + 1] - bgG;
    const db = data[i + 2] - bgB;
    const distSq = dr * dr + dg * dg + db * db;

    if (distSq <= tolSq) {
      data[i + 3] = 0;
    } else if (distSq <= featherSq) {
      const t = (Math.sqrt(distSq) - tolerance) / featherRange;
      data[i + 3] = Math.round(data[i + 3] * Math.min(1, t));
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/png');
  });
};

/**
 * Replace one color with another in an image, with tolerance and edge blending.
 */
const replaceImageColor = async (imageSrc, sourceColor, targetColor, tolerance = 30) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const { r: srcR, g: srcG, b: srcB } = sourceColor;
  const { r: tgtR, g: tgtG, b: tgtB } = targetColor;
  const tolSq = tolerance * tolerance;
  const featherRange = Math.max(tolerance * 0.4, 5);
  const featherSq = (tolerance + featherRange) * (tolerance + featherRange);

  for (let i = 0; i < data.length; i += 4) {
    // Skip fully transparent pixels
    if (data[i + 3] === 0) continue;

    const dr = data[i] - srcR;
    const dg = data[i + 1] - srcG;
    const db = data[i + 2] - srcB;
    const distSq = dr * dr + dg * dg + db * db;

    if (distSq <= tolSq) {
      // Within tolerance — full replacement
      data[i] = tgtR;
      data[i + 1] = tgtG;
      data[i + 2] = tgtB;
    } else if (distSq <= featherSq) {
      // Feather zone — blend between original and target
      const t = (Math.sqrt(distSq) - tolerance) / featherRange;
      data[i] = Math.round(tgtR + (data[i] - tgtR) * t);
      data[i + 1] = Math.round(tgtG + (data[i + 1] - tgtG) * t);
      data[i + 2] = Math.round(tgtB + (data[i + 2] - tgtB) * t);
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, 'image/png');
  });
};

const samplePixelColor = async (imageSrc, relX, relY) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const px = Math.min(Math.max(Math.round(relX * img.width), 0), img.width - 1);
  const py = Math.min(Math.max(Math.round(relY * img.height), 0), img.height - 1);
  const pixel = ctx.getImageData(px, py, 1, 1).data;
  return { r: pixel[0], g: pixel[1], b: pixel[2] };
};

/**
 * Check if an image has any transparent pixels (alpha < 255).
 */
const checkHasTransparency = async (imageSrc) => {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  // Sample edges + random interior pixels for speed
  const w = canvas.width;
  const h = canvas.height;
  // Check all edge pixels
  for (let x = 0; x < w; x++) {
    if (data[(0 * w + x) * 4 + 3] < 255) return true;         // top row
    if (data[((h - 1) * w + x) * 4 + 3] < 255) return true;   // bottom row
  }
  for (let y = 0; y < h; y++) {
    if (data[(y * w + 0) * 4 + 3] < 255) return true;         // left col
    if (data[(y * w + (w - 1)) * 4 + 3] < 255) return true;   // right col
  }
  // Sample interior (every 10th pixel)
  for (let i = 3; i < data.length; i += 40) {
    if (data[i] < 255) return true;
  }
  return false;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const rgbToHex = ({ r, g, b }) => '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');

const CHECKERBOARD = 'repeating-conic-gradient(#d4d4d4 0% 25%, white 0% 50%) 50% / 16px 16px';

// Build output spec string for the upload drop zone
const getOutputSpec = (aspectRatio, outputSize, maxOutputHeight, maxOutputWidth) => {
  if (outputSize) return `${outputSize.width} x ${outputSize.height}px`;
  const parts = [];
  if (maxOutputWidth) parts.push(`${maxOutputWidth}px wide`);
  if (maxOutputHeight) parts.push(`${maxOutputHeight}px tall`);
  if (aspectRatio === 1) parts.push('square');
  return parts.length ? 'max ' + parts.join(', ') : null;
};

const ImageUploadModal = ({
  isOpen, onClose, onUpload,
  title = 'Upload Image',
  suggestedName = '',
  aspectRatio,
  outputSize,
  maxOutputHeight,
  maxOutputWidth,
  maxSizeMB = 5,
  allowedTypes = '.png,.jpg,.jpeg,.svg,.webp'
}) => {
  // Source image
  const [sourceDataUrl, setSourceDataUrl] = useState(null);
  const [croppedDataUrl, setCroppedDataUrl] = useState(null);
  const [fileName, setFileName] = useState('');

  // Crop
  const [crop, setCrop] = useState(undefined);
  const cropImgRef = useRef(null);

  // Transforms
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  // Background removal
  const [bgRemovalTolerance, setBgRemovalTolerance] = useState(30);
  const [bgRemoved, setBgRemoved] = useState(false);
  const [hasTransparency, setHasTransparency] = useState(false);
  const [pickedColor, setPickedColor] = useState(null);
  const [pickingColor, setPickingColor] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [preRemovalDataUrl, setPreRemovalDataUrl] = useState(null);
  const [showTolerance, setShowTolerance] = useState(false);

  // Color replace
  const [crSourceColor, setCrSourceColor] = useState(null);
  const [crTargetColor, setCrTargetColor] = useState('#000000');
  const [crTolerance, setCrTolerance] = useState(30);
  const [crPickingSource, setCrPickingSource] = useState(false);
  const [crShowTolerance, setCrShowTolerance] = useState(false);
  const [crApplied, setCrApplied] = useState(false);
  const [crApplying, setCrApplying] = useState(false);

  // Output
  const [previewUrl, setPreviewUrl] = useState(null);
  const [outputInfo, setOutputInfo] = useState(null);

  // UI
  const [step, setStep] = useState('select');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const previewImgRef = useRef(null);
  const prevPreviewUrl = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSourceDataUrl(null); setCroppedDataUrl(null);
      setFileName(suggestedName);
      setCrop(undefined);
      setRotation(0); setFlipH(false); setFlipV(false);
      setBgRemovalTolerance(30); setBgRemoved(false); setHasTransparency(false);
      setPickedColor(null); setPickingColor(false); setRemovingBg(false);
      setPreRemovalDataUrl(null); setShowTolerance(false);
      setCrSourceColor(null); setCrTargetColor('#000000'); setCrTolerance(30);
      setCrPickingSource(false); setCrShowTolerance(false); setCrApplied(false); setCrApplying(false);
      setPreviewUrl(null); setOutputInfo(null);
      setStep('select'); setDragOver(false); setUploading(false); setError(null);
    }
  }, [isOpen, suggestedName]);

  // Rebuild preview when transforms change (edit step only)
  useEffect(() => {
    const src = croppedDataUrl || sourceDataUrl;
    if (!src || step !== 'edit') return;

    const constraints = (maxOutputHeight || maxOutputWidth) ? { maxHeight: maxOutputHeight, maxWidth: maxOutputWidth } : undefined;
    applyTransforms(src, { rotation, flipH, flipV }, constraints)
      .then(({ blob, width, height }) => {
        if (prevPreviewUrl.current) URL.revokeObjectURL(prevPreviewUrl.current);
        const url = URL.createObjectURL(blob);
        prevPreviewUrl.current = url;
        setPreviewUrl(url);
        setOutputInfo({ size: formatSize(blob.size), width, height, overLimit: blob.size > maxSizeMB * 1024 * 1024 });
      })
      .catch(() => {});
  }, [croppedDataUrl, sourceDataUrl, rotation, flipH, flipV, step, maxSizeMB, bgRemoved, crApplied, maxOutputHeight, maxOutputWidth]);

  const validateFile = useCallback((f) => {
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    const allowed = allowedTypes.split(',').map(t => t.trim());
    if (!allowed.includes(ext)) { setError(`File type ${ext} not allowed. Use: ${allowed.join(', ')}`); return false; }
    return true;
  }, [allowedTypes]);

  const handleFileSelect = useCallback((f) => {
    setError(null);
    if (!validateFile(f)) return;
    const ext = f.name.split('.').pop().toLowerCase();
    const svg = ext === 'svg';
    if (!fileName) setFileName(suggestedName || f.name.replace(/\.[^.]+$/, ''));

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setCroppedDataUrl(null);
      setCrop(undefined);
      if (svg) {
        // Rasterize SVG to PNG so it can be cropped and edited like any other image
        rasterizeSvg(dataUrl).then(pngDataUrl => {
          setSourceDataUrl(pngDataUrl);
          setStep('crop');
        }).catch(() => {
          setError('Failed to process SVG');
        });
      } else {
        setSourceDataUrl(dataUrl);
        setStep('crop');
      }
    };
    reader.readAsDataURL(f);
  }, [validateFile, fileName, suggestedName]);

  // Ctrl+V paste
  useEffect(() => {
    if (!isOpen || step !== 'select') return;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) handleFileSelect(new File([blob], (suggestedName || 'pasted-image') + '.png', { type: blob.type }));
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, step, handleFileSelect, suggestedName]);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]); };

  // When image loads in cropper, set initial crop to full image
  const onCropImageLoad = useCallback((e) => {
    cropImgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    if (aspectRatio) {
      const imgAspect = width / height;
      let cropW, cropH;
      if (imgAspect > aspectRatio) {
        cropH = height; cropW = height * aspectRatio;
      } else {
        cropW = width; cropH = width / aspectRatio;
      }
      setCrop({ unit: 'px', x: (width - cropW) / 2, y: (height - cropH) / 2, width: cropW, height: cropH });
    } else {
      setCrop({ unit: 'px', x: 0, y: 0, width, height });
    }
  }, [aspectRatio]);

  const handleCropApply = async () => {
    if (!crop || !crop.width || !crop.height || !sourceDataUrl || !cropImgRef.current) return;
    try {
      const displayedWidth = cropImgRef.current.width;
      const displayedHeight = cropImgRef.current.height;
      const blob = await cropImage(sourceDataUrl, crop, displayedWidth, displayedHeight, outputSize);
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        setCroppedDataUrl(dataUrl);
        setPreRemovalDataUrl(dataUrl);
        // Check if the cropped image already has transparency
        try {
          const transparent = await checkHasTransparency(dataUrl);
          setHasTransparency(transparent);
        } catch {
          setHasTransparency(false);
        }
        setStep('edit');
      };
      reader.readAsDataURL(blob);
    } catch { setError('Crop failed'); }
  };

  // Click on preview to pick a color (shared by bg removal and color replace)
  const handlePreviewClick = async (e) => {
    if (!pickingColor && !crPickingSource) return;
    const img = previewImgRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;

    try {
      const src = preRemovalDataUrl || croppedDataUrl || sourceDataUrl;
      const color = await samplePixelColor(src, relX, relY);
      if (pickingColor) {
        setPickedColor(color);
        setPickingColor(false);
      } else if (crPickingSource) {
        setCrSourceColor(color);
        setCrPickingSource(false);
      }
    } catch {
      setError('Could not sample color');
      setPickingColor(false);
      setCrPickingSource(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!pickedColor) return;
    const src = preRemovalDataUrl || croppedDataUrl || sourceDataUrl;
    if (!src) return;
    setRemovingBg(true);
    try {
      const dataUrl = await removeImageBackground(src, pickedColor, bgRemovalTolerance);
      setCroppedDataUrl(dataUrl);
      setBgRemoved(true);
      setHasTransparency(true);
    } catch {
      setError('Background removal failed');
    } finally {
      setRemovingBg(false);
    }
  };

  const handleColorReplace = async () => {
    if (!crSourceColor) return;
    const src = croppedDataUrl || sourceDataUrl;
    if (!src) return;
    // Parse target hex to RGB
    const hex = crTargetColor.replace('#', '');
    const targetRgb = { r: parseInt(hex.substring(0, 2), 16), g: parseInt(hex.substring(2, 4), 16), b: parseInt(hex.substring(4, 6), 16) };
    setCrApplying(true);
    try {
      const dataUrl = await replaceImageColor(src, crSourceColor, targetRgb, crTolerance);
      setCroppedDataUrl(dataUrl);
      setCrApplied(true);
    } catch {
      setError('Color replace failed');
    } finally {
      setCrApplying(false);
    }
  };

  const handleUpload = async () => {
    setUploading(true); setStep('uploading'); setError(null);
    try {
      const src = croppedDataUrl || sourceDataUrl;
      const constraints = (maxOutputHeight || maxOutputWidth) ? { maxHeight: maxOutputHeight, maxWidth: maxOutputWidth } : undefined;
      const { blob } = await applyTransforms(src, { rotation, flipH, flipV }, constraints);
      if (blob.size > maxSizeMB * 1024 * 1024) {
        setError(`Final image is ${formatSize(blob.size)} — exceeds ${maxSizeMB}MB limit.`);
        setStep('edit'); setUploading(false); return;
      }
      const finalFile = new File([blob], (fileName || 'image').replace(/[^a-zA-Z0-9_-]/g, '-') + '.png', { type: 'image/png' });
      await onUpload(finalFile);
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : err?.message || 'Upload failed');
      setStep('edit');
    } finally { setUploading(false); }
  };

  const handlePasteButton = () => {
    navigator.clipboard.read().then(items => {
      for (const item of items) {
        const imgType = item.types.find(t => t.startsWith('image/'));
        if (imgType) { item.getType(imgType).then(blob => handleFileSelect(new File([blob], (suggestedName || 'pasted-image') + '.png', { type: blob.type }))); break; }
      }
    }).catch(() => setError('No image in clipboard or permission denied'));
  };

  if (!isOpen) return null;

  const stepLabelStyle = { fontSize: '11px', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' };
  const stepNumStyle = { width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'var(--color-primary)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 };
  const stepNumDoneStyle = { ...stepNumStyle, backgroundColor: 'var(--color-success, #22c55e)' };
  const sectionStyle = { marginBottom: 'var(--spacing-md)', paddingBottom: 'var(--spacing-md)', borderBottom: '1px solid var(--color-gray-100)' };
  const smallBtnStyle = { padding: '4px 8px', border: '1px solid var(--color-gray-300)', borderRadius: 'var(--radius-sm)', background: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease', color: 'var(--color-gray-600)', fontSize: '11px', gap: '4px' };
  const activeBtnStyle = { ...smallBtnStyle, borderColor: 'var(--color-primary)', backgroundColor: 'var(--color-primary)', color: 'white' };
  const hasCrop = crop && crop.width > 0 && crop.height > 0;
  const outputSpec = getOutputSpec(aspectRatio, outputSize, maxOutputHeight, maxOutputWidth);

  return (
    <div className="sprint-add-task-overlay">
      <div className="sprint-add-task-modal" style={{ maxWidth: '800px', width: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3><ImageIcon size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />{title}</h3>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body" style={{ flex: 1, overflow: 'hidden', padding: 0 }}>
          {error && <div style={{ padding: 'var(--spacing-sm) var(--spacing-lg)', backgroundColor: 'var(--color-error-light)', color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>{error}</div>}

          {/* ===== STEP 1: SELECT ===== */}
          {step === 'select' && (
            <div style={{ padding: 'var(--spacing-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              <div onClick={() => inputRef.current?.click()} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                style={{ border: `2px dashed ${dragOver ? 'var(--color-primary)' : 'var(--color-gray-300)'}`, backgroundColor: dragOver ? 'var(--color-primary-light)' : 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-xl)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer', transition: 'all 0.15s ease', minHeight: '200px' }}>
                <Upload size={40} style={{ color: dragOver ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-gray-700)' }}>Drop image here or click to browse</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>Max {maxSizeMB}MB • {allowedTypes.split(',').join(', ')}</p>
                <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-sm) var(--spacing-md)', backgroundColor: 'white', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', textAlign: 'center', lineHeight: 1.5 }}>
                  Image will be converted to <strong>PNG with transparency</strong>
                  {outputSpec && <><br />Output: <strong>{outputSpec}</strong></>}
                </div>
              </div>
              <input ref={inputRef} type="file" accept={allowedTypes} style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); e.target.value = ''; }} />
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'center' }}>
                <button className="btn btn-secondary btn-sm" onClick={handlePasteButton}><Clipboard size={14} /> Paste from clipboard</button>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', alignSelf: 'center' }}>or Ctrl+V</span>
              </div>
            </div>
          )}

          {/* ===== STEP 2: CROP (forced) ===== */}
          {step === 'crop' && sourceDataUrl && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '450px' }}>
              <div style={{ padding: 'var(--spacing-sm) var(--spacing-lg)', backgroundColor: 'var(--color-primary-light)', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                <CropIcon size={14} style={{ color: 'var(--color-primary)' }} />
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-gray-700)' }}>
                  Drag the handles to crop, then click <strong>Apply Crop</strong> to continue
                </span>
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-gray-100)', overflow: 'auto', padding: 'var(--spacing-md)' }}>
                <ReactCrop crop={crop} onChange={c => setCrop(c)} aspect={aspectRatio} style={{ maxHeight: '100%' }}>
                  <img src={sourceDataUrl} alt="Crop source" onLoad={onCropImageLoad} style={{ maxWidth: '100%', maxHeight: '400px', display: 'block' }} />
                </ReactCrop>
              </div>
            </div>
          )}

          {/* ===== STEP 3: EDIT ===== */}
          {step === 'edit' && (
            <div style={{ display: 'flex', height: '100%', minHeight: '400px' }}>
              {/* LEFT: Preview */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--color-gray-100)', position: 'relative', overflow: 'hidden', cursor: (pickingColor || crPickingSource) ? 'crosshair' : 'default' }}>
                <div style={{ padding: 'var(--spacing-lg)', maxWidth: '100%', maxHeight: '100%' }}>
                  {previewUrl && (
                    <img
                      ref={previewImgRef}
                      src={previewUrl}
                      alt="Preview"
                      onClick={handlePreviewClick}
                      style={{
                        maxWidth: '100%', maxHeight: '380px',
                        borderRadius: 'var(--radius-sm)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        background: CHECKERBOARD,
                        cursor: (pickingColor || crPickingSource) ? 'crosshair' : 'default'
                      }}
                    />
                  )}
                </div>
                {(pickingColor || crPickingSource) && (
                  <div style={{ position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                    {pickingColor ? 'Click on the background color you want to remove' : 'Click on the color you want to change'}
                  </div>
                )}
              </div>

              {/* RIGHT: Workflow Controls */}
              <div style={{ width: '210px', flexShrink: 0, borderLeft: '1px solid var(--color-gray-200)', padding: 'var(--spacing-md)', overflowY: 'auto', fontSize: 'var(--text-xs)', display: 'flex', flexDirection: 'column' }}>

                {/* 1. Crop */}
                                <div style={sectionStyle}>
                    <div style={stepLabelStyle}>
                      <span style={stepNumDoneStyle}><Check size={10} /></span>
                      Crop
                    </div>
                    <button style={smallBtnStyle} onClick={() => {
                      setCroppedDataUrl(null); setPreRemovalDataUrl(null);
                      setBgRemoved(false); setPickedColor(null); setHasTransparency(false);
                      setCrop(undefined); setShowTolerance(false);
                      setCrSourceColor(null); setCrApplied(false); setCrShowTolerance(false);
                      setStep('crop');
                    }}>
                      <CropIcon size={12} /> Re-crop
                    </button>
                  </div>

                {/* 2. Remove Background */}
                <div style={sectionStyle}>
                    <div style={stepLabelStyle}>
                      <span style={(bgRemoved || hasTransparency) ? stepNumDoneStyle : stepNumStyle}>
                        {(bgRemoved || hasTransparency) ? <Check size={10} /> : '2'}
                      </span>
                      Remove Background
                    </div>

                    {/* Already transparent notice */}
                    {hasTransparency && !bgRemoved && (
                      <div style={{ fontSize: '10px', color: 'var(--color-success, #22c55e)', marginBottom: '6px', lineHeight: 1.4 }}>
                        This image already has transparency. You can skip this step or remove additional background.
                      </div>
                    )}

                    {/* Pick color */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                      <button
                        style={pickingColor ? activeBtnStyle : smallBtnStyle}
                        onClick={() => setPickingColor(!pickingColor)}
                      >
                        <Pipette size={12} />
                        {pickingColor ? 'Click image...' : 'Pick Color'}
                      </button>
                      {pickedColor && (
                        <span style={{ width: '16px', height: '16px', borderRadius: '3px', backgroundColor: rgbToHex(pickedColor), border: '1px solid var(--color-gray-300)', display: 'inline-block', flexShrink: 0 }} title={rgbToHex(pickedColor)} />
                      )}
                    </div>

                    {/* Remove BG button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: showTolerance ? '6px' : 0 }}>
                      <button
                        style={!pickedColor ? { ...smallBtnStyle, opacity: 0.4, pointerEvents: 'none' } : smallBtnStyle}
                        onClick={handleRemoveBackground}
                        disabled={removingBg || !pickedColor}
                      >
                        <Eraser size={12} />
                        {removingBg ? 'Removing...' : bgRemoved ? 'Re-remove' : 'Remove BG'}
                      </button>
                      {/* Fine-tune toggle */}
                      <button
                        style={{ ...smallBtnStyle, padding: '4px', width: '24px', height: '24px' }}
                        onClick={() => setShowTolerance(!showTolerance)}
                        title="Fine-tune edge tolerance"
                      >
                        <Plus size={10} style={{ transform: showTolerance ? 'rotate(45deg)' : 'none', transition: 'transform 0.15s ease' }} />
                      </button>
                    </div>

                    {/* Tolerance (hidden by default) */}
                    {showTolerance && (
                      <div style={{ padding: '6px 8px', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)' }}>
                        <div style={{ fontSize: '10px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>Edge tolerance — how aggressively similar colors are removed</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Less</span>
                          <input type="range" min={5} max={80} value={bgRemovalTolerance} onChange={e => setBgRemovalTolerance(parseInt(e.target.value))} style={{ flex: 1 }} />
                          <span style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>More</span>
                        </div>
                      </div>
                    )}
                  </div>

                {/* 3. Color Replace */}
                <div style={sectionStyle}>
                    <div style={stepLabelStyle}>
                      <span style={crApplied ? stepNumDoneStyle : stepNumStyle}>
                        {crApplied ? <Check size={10} /> : '3'}
                      </span>
                      Replace Color
                    </div>

                    {/* Pick source color */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                      <button
                        style={crPickingSource ? activeBtnStyle : smallBtnStyle}
                        onClick={() => { setCrPickingSource(!crPickingSource); setPickingColor(false); }}
                      >
                        <Pipette size={12} />
                        {crPickingSource ? 'Click image...' : 'From'}
                      </button>
                      {crSourceColor && (
                        <span style={{ width: '16px', height: '16px', borderRadius: '3px', backgroundColor: rgbToHex(crSourceColor), border: '1px solid var(--color-gray-300)', display: 'inline-block', flexShrink: 0 }} title={rgbToHex(crSourceColor)} />
                      )}
                      <span style={{ fontSize: '10px', color: 'var(--color-gray-400)', margin: '0 2px' }}>→</span>
                      <div style={{ position: 'relative', width: '24px', height: '24px', flexShrink: 0 }}>
                        <input
                          type="color"
                          value={crTargetColor}
                          onChange={e => setCrTargetColor(e.target.value)}
                          title="Target color"
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', padding: 0, border: '1px solid var(--color-gray-300)', borderRadius: '3px', cursor: 'pointer' }}
                        />
                      </div>
                    </div>

                    {/* Apply button + tolerance toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: crShowTolerance ? '6px' : 0 }}>
                      <button
                        style={!crSourceColor ? { ...smallBtnStyle, opacity: 0.4, pointerEvents: 'none' } : smallBtnStyle}
                        onClick={handleColorReplace}
                        disabled={crApplying || !crSourceColor}
                      >
                        <Palette size={12} />
                        {crApplying ? 'Applying...' : crApplied ? 'Re-apply' : 'Replace'}
                      </button>
                      <button
                        style={{ ...smallBtnStyle, padding: '4px', width: '24px', height: '24px' }}
                        onClick={() => setCrShowTolerance(!crShowTolerance)}
                        title="Fine-tune color matching"
                      >
                        <Plus size={10} style={{ transform: crShowTolerance ? 'rotate(45deg)' : 'none', transition: 'transform 0.15s ease' }} />
                      </button>
                    </div>

                    {/* Tolerance (hidden by default) */}
                    {crShowTolerance && (
                      <div style={{ padding: '6px 8px', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)' }}>
                        <div style={{ fontSize: '10px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>How closely colors must match to be replaced</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Exact</span>
                          <input type="range" min={5} max={80} value={crTolerance} onChange={e => setCrTolerance(parseInt(e.target.value))} style={{ flex: 1 }} />
                          <span style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Loose</span>
                        </div>
                      </div>
                    )}
                  </div>

                {/* 4. Orientation */}
                <div style={sectionStyle}>
                    <div style={stepLabelStyle}>
                      <span style={(rotation !== 0 || flipH || flipV) ? stepNumDoneStyle : stepNumStyle}>
                        {(rotation !== 0 || flipH || flipV) ? <Check size={10} /> : '4'}
                      </span>
                      Orientation
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button style={smallBtnStyle} onClick={() => setRotation((rotation + 270) % 360)} title="Rotate left 90°"><RotateCcw size={14} /></button>
                      <button style={smallBtnStyle} onClick={() => setRotation((rotation + 90) % 360)} title="Rotate right 90°"><RotateCw size={14} /></button>
                      <button style={flipH ? activeBtnStyle : smallBtnStyle} onClick={() => setFlipH(!flipH)} title="Flip horizontal"><FlipHorizontal size={14} /></button>
                      <button style={flipV ? activeBtnStyle : smallBtnStyle} onClick={() => setFlipV(!flipV)} title="Flip vertical"><FlipVertical size={14} /></button>
                    </div>
                    {rotation !== 0 && <div style={{ marginTop: '3px', color: 'var(--color-primary)', fontSize: '10px' }}>{rotation}°</div>}
                  </div>

                {/* File Name + Details */}
                <div style={{ marginTop: 'auto', paddingTop: 'var(--spacing-md)' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>File Name</div>
                    <input className="form-input" value={fileName} onChange={e => setFileName(e.target.value)} placeholder="filename" style={{ height: '28px', fontSize: '11px' }} />
                  </div>
                  {outputInfo && (
                    <div style={{ color: 'var(--color-gray-500)', fontSize: '10px', lineHeight: 1.6 }}>
                      {outputInfo.width} x {outputInfo.height} &bull; {outputInfo.size} &bull; PNG
                      {outputInfo.overLimit && <span style={{ color: 'var(--color-error)', fontWeight: 600 }}> (over {maxSizeMB}MB!)</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== UPLOADING ===== */}
          {step === 'uploading' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--spacing-xl)', minHeight: '200px', gap: 'var(--spacing-md)' }}>
              <Upload size={32} className="spinning" style={{ color: 'var(--color-primary)' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>Uploading...</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          {step === 'crop' && (
            <button className="btn btn-primary" onClick={handleCropApply} disabled={!hasCrop}>
              <CropIcon size={14} /> Apply Crop
            </button>
          )}
          {step === 'edit' && (
            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || outputInfo?.overLimit}>
              <Upload size={14} /> Upload
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal;
